import React, { useState, useEffect, RefObject } from 'react';
import { Save, Check, CircleMinus, CircleCheck } from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { confirmWarning, confirmActive } from '../common/confirm';
import {
    Agency,
    ProgramTemplate,
    ProgramTemplateCreatePayload,
    CADENCE_OPTIONS,
    OWNERSHIP_SCOPE_OPTIONS,
    OwnershipScope,
    CadenceValue,
} from '../../services/auditService';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

interface ProgramModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: ProgramTemplateCreatePayload, id?: number) => void | Promise<void>;
    initialData: ProgramTemplate | null;
    agenciesList: Agency[];
    /** Non-empty ⇒ the caller is an audit lead: the Ownership Scope field is hidden
     * (always Agency-Private) and the Owning Agency is restricted to these agencies
     * (the ones the lead heads). One agency ⇒ auto-selected, no picker. So nothing
     * they create is ever GRC-visible. Empty/undefined ⇒ normal GRC authoring. */
    leadAgencies?: { id: number; name: string }[];
    title?: string;
    saveLabel?: string;
    /** When true, renders without modal chrome (used inside ProgramConfigModal Details tab). */
    embedded?: boolean;
    /** When true all fields are disabled (view mode). Parent toggles to enable editing. */
    disabled?: boolean;
    /** Hide the embedded save button — parent owns the footer. */
    hideEmbeddedFooter?: boolean;
    /** Ref to the embedded form so parent can call requestSubmit(). */
    embeddedFormRef?: RefObject<HTMLFormElement | null>;
    /** Soft-delete: deactivate this program (edit mode only). Parent owns confirm + reload + toast. */
    onInactivate?: (id: number) => Promise<void>;
    /** Soft-delete: reactivate this program (edit mode only). Parent owns confirm + reload + toast. */
    onReactivate?: (id: number) => Promise<void>;
}

const blank: ProgramTemplateCreatePayload = {
    audit_program_code: '',
    audit_program_name: '',
    audit_program_description: '',
    agency_id: null,
    effective_start_date: null,
    cadence: 'Ad-hoc',
    ownership_scope: 'GRC-Common',
    is_active: 1,
    is_approved: 0,
};

const ProgramModal: React.FC<ProgramModalProps> = ({
    isOpen, onClose, onSave, initialData, agenciesList, leadAgencies,
    title, saveLabel, embedded, disabled = false,
    hideEmbeddedFooter = false, embeddedFormRef,
    onInactivate, onReactivate,
}) => {
    // Audit-lead mode — scope is implicitly Agency-Private; the owning agency is
    // restricted to the lead's own agencies (auto-picked when there is only one).
    const leadMode = (leadAgencies?.length ?? 0) > 0;
    const singleLeadAgency = leadAgencies?.length === 1;
    const [formData, setFormData] = useState<ProgramTemplateCreatePayload>(blank);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const isEdit = !!initialData;
    const isCurrentlyActive = (initialData?.is_active ?? 1) === 1;
    // Approved programs are frozen: read-only, no deactivate. Reopen via the status dot.
    const isApproved = isEdit && (initialData as any)?.workflow_state === 'approved';
    const vh = useFieldValidation();
    const { allowed: canModify, loading: permLoading } = usePermission('ADT_PROGRAMS', 'view+modify');
    // Audit-lead authoring is authorized POSITIONALLY (agency head) — an external
    // audit lead holds no GRC ADT_PROGRAMS grant. In lead mode the GRC RBAC gate
    // must NOT disable Submit; the backend enforces agency-head authorization.
    const authorized = leadMode || canModify;
    const gateLoading = leadMode ? false : permLoading;

    useEffect(() => {
        if (!isOpen) return;
        if (initialData) {
            const { id, created_on, current_version, ...rest } = initialData as any;
            setFormData({ ...blank, ...rest });
        } else if (leadMode) {
            // Audit lead — force Agency-Private; default to the first headed agency.
            setFormData({
                ...blank,
                ownership_scope: 'Agency-Private',
                agency_id: leadAgencies![0].id,
                effective_start_date: new Date().toISOString().split('T')[0],
            });
        } else {
            setFormData({
                ...blank,
                agency_id: null,
                effective_start_date: new Date().toISOString().split('T')[0],
            });
        }
        setSubmitting(false);
        setToggling(false);
        vh.reset();
    }, [initialData, isOpen, agenciesList, leadMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = <K extends keyof ProgramTemplateCreatePayload>(k: K, v: ProgramTemplateCreatePayload[K]) =>
        setFormData(prev => ({ ...prev, [k]: v }));

    // BRD#912: Agency-Private requires an agency
    const needsAgency = formData.ownership_scope === 'Agency-Private';
    const canSave = !!(
        formData.audit_program_name.trim() &&
        formData.cadence &&
        (!needsAgency || formData.agency_id)
    );

    const handleSave = async () => {
        if (!canSave) return;
        if (!authorized) return;
        // Validate all fields before submit
        const specs = [
            { name: 'name', code: 109, value: formData.audit_program_name, required: true },
            { name: 'description', code: 110, value: formData.audit_program_description },
            { name: 'effective_start_date', code: 305, value: formData.effective_start_date },
        ];
        if (!vh.validateAll(specs)) return;
        // Custom validation for required select fields
        if (!formData.cadence) {
            vh.setError('cadence', 'This field is required');
            return;
        }
        if (!formData.ownership_scope) {
            vh.setError('ownership_scope', 'This field is required');
            return;
        }
        if (needsAgency && !formData.agency_id) {
            vh.setError('agency_id', 'This field is required for Agency-Private scope');
            return;
        }
        setSubmitting(true);
        try {
            await onSave(
                { ...formData, effective_start_date: formData.effective_start_date || null },
                initialData?.id,
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSave();
    };

    const handleToggleActive = async () => {
        if (!initialData || isApproved) return;
        const confirmFn = isCurrentlyActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: isCurrentlyActive ? 'Inactivate Program Template?' : 'Reactivate Program Template?',
            text: isCurrentlyActive
                ? 'The record will be retained but marked Inactive. In-flight instances continue unaffected.'
                : 'The program template will become active again.',
            confirmText: isCurrentlyActive ? 'Yes, Inactivate' : 'Yes, Reactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            if (isCurrentlyActive) {
                await onInactivate?.(initialData.id);
            } else {
                await onReactivate?.(initialData.id);
            }
        } finally {
            setToggling(false);
        }
    };

    // Only approved + active agencies belong in the Assigned Agency picker.
    // agenciesList is the shared list used by the Agencies management tab too, so
    // it carries draft/submitted/rejected rows — filter them out here.
    const agencyOptions = agenciesList
        .filter(a => (a.is_active ?? 1) === 1 && a.workflow_state === 'approved')
        .map(a => ({ value: a.id, label: a.audit_agency_name }));

    const formBody = (
        <fieldset disabled={isApproved} className="contents">
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            {/* Row 1 — system code (APT-N) shown read-only when editing; auto-generated on approve. */}
            {isEdit && (
                <ValidatedInput
                    vh={vh}
                    name="code"
                    code={501}
                    label="Code"
                    value={(initialData as any)?.code || '—'}
                    onChange={() => { /* read-only */ }}
                    disabled
                    data-testid="INP_program_code"
                />
            )}
            <ValidatedDatePicker
                vh={vh}
                name="effective_start_date"
                code={305}
                label="Effective Start"
                value={formData.effective_start_date || ''}
                onChange={e => set('effective_start_date', e.target.value || null)}
                disabled={disabled}
                data-testid="INP_program_effective_start"
            />
            {/* Row 2 */}
            <ValidatedInput
                vh={vh}
                name="name"
                code={109}
                required
                label="Program Name"
                value={formData.audit_program_name}
                onChange={e => set('audit_program_name', e.target.value)}
                placeholder="IT General Controls 2024"
                disabled={disabled}
                data-testid="INP_program_name"
            />
            <FormControl.SearchSelect
                label="Cadence"
                required
                options={CADENCE_OPTIONS}
                value={formData.cadence ?? null}
                onChange={v => {
                    set('cadence', (v as CadenceValue) || 'Ad-hoc');
                    vh.clearError('cadence');
                }}
                placeholder="— Select cadence —"
                disabled={disabled}
                data-testid="SEL_program_cadence"
                error={vh.errors['cadence']}
            />
            {/* Row 3 — Ownership Scope is hidden in audit-lead mode (always Agency-Private). */}
            {!leadMode && (
                <div>
                    <FormControl.SearchSelect
                        label="Ownership Scope"
                        required
                        // GRC persona may only author GRC-Common. Agency-Private is
                        // never offered here — that scope belongs to the audit-lead
                        // portal (leadMode hides this field entirely).
                        options={OWNERSHIP_SCOPE_OPTIONS.filter(o => o.value === 'GRC-Common')}
                        value={formData.ownership_scope ?? null}
                        onChange={v => {
                            if (isEdit) return;
                            set('ownership_scope', (v as OwnershipScope) || 'GRC-Common');
                            if (v !== 'Agency-Private') set('agency_id', null);
                            vh.clearError('ownership_scope');
                        }}
                        placeholder="— Select scope —"
                        disabled={isEdit || disabled}
                        data-testid="SEL_program_ownership_scope"
                        error={vh.errors['ownership_scope']}
                    />
                    {isEdit && (
                        <p className="text-xs text-gray-400 mt-1">Ownership scope is locked after creation (BRD#924)</p>
                    )}
                </div>
            )}
            {/* Owning Agency — in audit-lead mode restricted to the lead's own agencies
                (a single agency is auto-selected and shown read-only). */}
            {leadMode ? (
                singleLeadAgency ? (
                    <FormControl.Input
                        type="text"
                        label="Owning Agency"
                        value={leadAgencies![0].name}
                        disabled
                        data-testid="SEL_program_agency"
                    />
                ) : (
                    <FormControl.SearchSelect
                        label="Owning Agency (required)"
                        required
                        options={leadAgencies!.map(a => ({ value: a.id, label: a.name }))}
                        value={formData.agency_id}
                        onChange={v => { set('agency_id', v ? Number(v) : null); vh.clearError('agency_id'); }}
                        placeholder="— Select your agency —"
                        disabled={disabled || isEdit}
                        data-testid="SEL_program_agency"
                        error={vh.errors['agency_id']}
                    />
                )
            ) : (
                <FormControl.SearchSelect
                    label={needsAgency ? 'Owning Agency (required)' : 'Assigned Agency'}
                    required={needsAgency}
                    options={agencyOptions}
                    value={formData.agency_id}
                    onChange={v => {
                        set('agency_id', v ? Number(v) : null);
                        vh.clearError('agency_id');
                    }}
                    placeholder="— None —"
                    clearable={!needsAgency}
                    disabled={disabled}
                    data-testid="SEL_program_agency"
                    error={vh.errors['agency_id']}
                />
            )}
            {/* Row 4 — full width */}
            <div className="col-span-2">
                <ValidatedTextarea
                    vh={vh}
                    name="description"
                    code={110}
                    label="Description"
                    value={formData.audit_program_description || ''}
                    onChange={e => set('audit_program_description', e.target.value)}
                    disabled={disabled}
                    data-testid="INP_program_description"
                />
            </div>
        </div>
        </fieldset>
    );

    if (embedded) {
        if (!isOpen) return null;
        return (
            <div>
                <form ref={embeddedFormRef} onSubmit={handleSubmit}>
                    {formBody}
                </form>
                {!hideEmbeddedFooter && !isApproved && (
                    <div className="flex justify-end pt-4 mt-4 border-t border-gray-100">
                        <Button
                            variant={isEdit ? 'update' : 'fill'}
                            icon={isEdit ? Check : Save}
                            onClick={handleSave}
                            disabled={gateLoading || !authorized || !canSave || submitting}
                            title={!authorized ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_program_embedded_submit"
                        >
                            {submitting
                                ? (isEdit ? 'Updating...' : 'Submitting...')
                                : (saveLabel || (isEdit ? 'Update' : 'Submit'))}
                        </Button>
                    </div>
                )}
            </div>
        );
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>
                {title
                    ? title
                    : isEdit
                        ? <>Editing "<span className="item-highlight">{initialData!.audit_program_name}</span>"</>
                        : 'Create Audit Program'}
            </Modal.Header>
            <Modal.Body>
                <form id="program-form" onSubmit={handleSubmit} data-testid="MOD_program_form">
                    {formBody}
                </form>
            </Modal.Body>
            <Modal.Footer className={isEdit && !isApproved ? 'app-modal-footer--split' : ''}>
                {isApproved ? (
                    <span style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic', alignSelf: 'center' }} data-testid="LBL_program_readonly">
                        Approved — read-only. Reopen from the status dot to edit.
                    </span>
                ) : (
                    <>
                        {isEdit && (onInactivate || onReactivate) && (
                            <Button
                                variant={isCurrentlyActive ? 'inactive-outline' : 'active-outline'}
                                icon={isCurrentlyActive ? CircleMinus : CircleCheck}
                                type="button"
                                onClick={handleToggleActive}
                                disabled={toggling || submitting}
                                data-testid="BTN_program_toggle_active"
                            >
                                {toggling ? '...' : isCurrentlyActive ? 'Inactive' : 'Active'}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            form="program-form"
                            variant={isEdit ? 'update' : 'fill'}
                            icon={isEdit ? Check : Save}
                            disabled={gateLoading || !authorized || !canSave || submitting || toggling}
                            title={!authorized ? NOT_AUTHORISED_MSG : undefined}
                            data-testid="BTN_program_submit"
                        >
                            {submitting
                                ? (isEdit ? 'Updating...' : 'Submitting...')
                                : (saveLabel || (isEdit ? 'Update' : 'Submit'))}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default ProgramModal;
